/**
 * 정렬·필터·검색·집계·그룹 — 모두 "뷰(물리 행 번호 배열)"를 입력받아 새 뷰를 돌려준다.
 * 원본 Dataset 은 건드리지 않는다.
 */
import { StrColumn, NumColumn } from './dataset.js';
const dispColl = new Intl.Collator('ko', { numeric: true, sensitivity: 'base' });
const dispCache = new WeakMap();
/** 사전(값 목록) → 표시 글자 순위 (순수). dict[0] 은 빈 값(null) = 맨 뒤(0xffffffff). 같은 글자면 원래 값 순 */
export function displayRank(dict, text) {
    const n = dict.length, keys = new Array(n), order = [];
    for (let i = 1; i < n; i++) {
        keys[i] = String(text(dict[i]) ?? '');
        order.push(i);
    }
    order.sort((a, b) => dispColl.compare(keys[a], keys[b]) || dispColl.compare(dict[a], dict[b]));
    const r = new Uint32Array(n);
    r[0] = 0xffffffff;
    for (let i = 0; i < order.length; i++)
        r[order[i]] = i;
    return r;
}
// ───────────────────────── 정렬: 안정 LSD 기수 정렬 ─────────────────────────
export function sortView(ds, view, keys) {
    const idx = Uint32Array.from(view);
    const n = idx.length;
    if (n < 2 || !keys.length)
        return idx;
    const key = new Uint32Array(n), hi = new Uint32Array(n);
    const tIdx = new Uint32Array(n), tKey = new Uint32Array(n), tPay = new Uint32Array(n);
    const f = new Float64Array(1), u = new Uint32Array(f.buffer);
    for (let k = keys.length - 1; k >= 0; k--) {
        const col = ds.cols[keys[k].col];
        if (!col)
            continue;
        const desc = keys[k].dir === 'desc';
        const tf = keys[k].text;
        if (col instanceof StrColumn) {
            let rank;
            if (tf) { // 표시 글자 순위: 사전 크기만큼만 변환 (사전이 늘거나 함수가 바뀌면 다시)
                const c = dispCache.get(col);
                if (c && c.fn === tf && c.n === col.dict.length)
                    rank = c.rank;
                else {
                    rank = displayRank(col.dict, tf);
                    dispCache.set(col, { fn: tf, n: col.dict.length, rank });
                }
            }
            else
                rank = col.rank();
            const codes = col.codes;
            for (let i = 0; i < n; i++) {
                let x = rank[codes[idx[i]]];
                if (desc && x !== 0xffffffff)
                    x = 0xfffffffe - x;
                key[i] = x;
            }
            radix(idx, key, null, tIdx, tKey, tPay);
        }
        else if (tf) { // 숫자 값의 표시 글자 정렬 (코드가 숫자인 콤보 등): 서로 다른 값만 변환
            const vals = col.vals, uniq = new Map();
            for (let i = 0; i < n; i++) {
                const v = vals[idx[i]];
                if (v === v && !uniq.has(v))
                    uniq.set(v, 0);
            }
            const ord = [...uniq.keys()].map(v => [v, String(tf(col.type === 'boolean' ? v !== 0 : v) ?? '')]).sort((a, b) => dispColl.compare(a[1], b[1]) || a[0] - b[0]);
            ord.forEach(([v], j) => uniq.set(v, j));
            for (let i = 0; i < n; i++) {
                const v = vals[idx[i]];
                let x = v === v ? uniq.get(v) : 0xffffffff;
                if (desc && x !== 0xffffffff)
                    x = 0xfffffffe - x;
                key[i] = x;
            }
            radix(idx, key, null, tIdx, tKey, tPay);
        }
        else {
            const vals = col.vals;
            for (let i = 0; i < n; i++) {
                const v = vals[idx[i]];
                if (v !== v) {
                    key[i] = 0xffffffff;
                    hi[i] = 0xffffffff;
                    continue;
                } // 빈 값은 항상 맨 뒤
                f[0] = v;
                let l = u[0], h = u[1];
                if (h & 0x80000000) {
                    h = ~h >>> 0;
                    l = ~l >>> 0;
                }
                else
                    h = (h | 0x80000000) >>> 0;
                if (desc) {
                    h = (0xfffffffe - h) >>> 0;
                    l = ~l >>> 0;
                }
                key[i] = l;
                hi[i] = h;
            }
            radix(idx, key, hi, tIdx, tKey, tPay);
            radix(idx, hi, null, tIdx, tKey, tPay);
        }
    }
    return idx;
}
const CNT = new Uint32Array(65536);
function radix(idx, key, pay, tIdx, tKey, tPay) {
    const n = idx.length;
    for (let pass = 0; pass < 2; pass++) {
        const sh = pass * 16;
        CNT.fill(0);
        for (let i = 0; i < n; i++)
            CNT[(key[i] >>> sh) & 0xffff]++;
        let s = 0;
        for (let b = 0; b < 65536; b++) {
            const c = CNT[b];
            CNT[b] = s;
            s += c;
        }
        if (pay)
            for (let i = 0; i < n; i++) {
                const p = CNT[(key[i] >>> sh) & 0xffff]++;
                tIdx[p] = idx[i];
                tKey[p] = key[i];
                tPay[p] = pay[i];
            }
        else
            for (let i = 0; i < n; i++) {
                const p = CNT[(key[i] >>> sh) & 0xffff]++;
                tIdx[p] = idx[i];
                tKey[p] = key[i];
            }
        idx.set(tIdx);
        key.set(tKey);
        if (pay)
            pay.set(tPay);
    }
}
export function filterView(ds, view, preds) {
    const tests = preds.map(p => compile(ds, p)).filter(Boolean);
    if (!tests.length)
        return view;
    const out = new Uint32Array(view.length);
    let k = 0;
    outer: for (let i = 0; i < view.length; i++) {
        const r = view[i];
        for (let t = 0; t < tests.length; t++)
            if (!tests[t](r))
                continue outer;
        out[k++] = r;
    }
    return out.slice(0, k);
}
function compile(ds, p) {
    const col = ds.cols[p.col];
    if (!col)
        return null;
    if (col instanceof NumColumn) {
        const v = col.vals;
        const x = Number(p.value), y = Number(p.value2);
        switch (p.op) {
            case '=': return r => v[r] === x;
            case '!=': return r => v[r] !== x;
            case '>': return r => v[r] > x;
            case '>=': return r => v[r] >= x;
            case '<': return r => v[r] < x;
            case '<=': return r => v[r] <= x;
            case 'between': return r => v[r] >= x && v[r] <= y;
            case 'notBetween': return r => v[r] === v[r] && (v[r] < x || v[r] > y);
            case 'empty': return r => v[r] !== v[r];
            case 'notEmpty': return r => v[r] === v[r];
            case 'in': {
                const s = new Set(p.value.map(Number));
                return r => s.has(v[r]);
            }
            case 'notIn': {
                const s = new Set(p.value.map(Number));
                return r => !s.has(v[r]);
            }
            case 'contains': {
                const q = String(p.value);
                return r => v[r] === v[r] && String(v[r]).includes(q);
            }
            case 'startsWith': {
                const q = String(p.value);
                return r => v[r] === v[r] && String(v[r]).startsWith(q);
            }
            case 'endsWith': {
                const q = String(p.value);
                return r => v[r] === v[r] && String(v[r]).endsWith(q);
            }
            case 'like':
            case 'notLike': {
                const re = likeRe(String(p.value));
                const neg = p.op === 'notLike';
                return r => v[r] === v[r] && re.test(String(v[r])) !== neg;
            }
            default: return null;
        }
    }
    // 문자열: 사전 항목마다 한 번만 판정 → 코드 마스크
    const c = col;
    const mask = new Uint8Array(c.dict.length);
    const q = p.value == null ? '' : String(p.value).toLowerCase();
    const q2 = p.value2 == null ? '' : String(p.value2).toLowerCase();
    const set = Array.isArray(p.value) ? new Set(p.value.map(String)) : null;
    const re = p.op === 'like' || p.op === 'notLike' ? likeRe(String(p.value ?? '')) : null;
    // 날짜 컬럼: 조건 값이 날짜만(10자)이면 시간 부분을 무시하고 비교 (QuantumGrid IgnoreTimeForFiltering)
    const dateOnly = c.type === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(q) && (!q2 || /^\d{4}-\d{2}-\d{2}$/.test(q2));
    for (let i = 0; i < c.dict.length; i++) {
        const raw = c.dict[i];
        let s = raw == null ? '' : raw.toLowerCase();
        if (dateOnly && s.length > 10)
            s = s.slice(0, 10);
        if (dateOnly && /^\d{8}/.test(s))
            s = `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
        let ok;
        switch (p.op) {
            case '=':
                ok = s === q;
                break;
            case '!=':
                ok = s !== q;
                break;
            case 'contains':
                ok = s.includes(q);
                break;
            case 'notContains':
                ok = !s.includes(q);
                break;
            case 'startsWith':
                ok = s.startsWith(q);
                break;
            case 'endsWith':
                ok = s.endsWith(q);
                break;
            case 'like':
                ok = raw != null && re.test(raw);
                break;
            case 'notLike':
                ok = raw == null || !re.test(raw);
                break;
            case 'notBetween':
                ok = raw != null && (cmp(s, q) < 0 || cmp(s, q2) > 0);
                break;
            case '>':
                ok = raw != null && cmp(s, q) > 0;
                break;
            case '>=':
                ok = raw != null && cmp(s, q) >= 0;
                break;
            case '<':
                ok = raw != null && cmp(s, q) < 0;
                break;
            case '<=':
                ok = raw != null && cmp(s, q) <= 0;
                break;
            case 'between':
                ok = raw != null && cmp(s, q) >= 0 && cmp(s, q2) <= 0;
                break;
            case 'in':
                ok = !!set && raw != null && set.has(raw);
                break;
            case 'notIn':
                ok = !set || raw == null || !set.has(raw);
                break;
            case 'empty':
                ok = raw == null;
                break;
            case 'notEmpty':
                ok = raw != null;
                break;
            default: ok = true;
        }
        mask[i] = ok ? 1 : 0;
    }
    const codes = c.codes;
    return r => mask[codes[r]] === 1;
}
/** Like 패턴: * 또는 % = 여러 글자, ? 또는 _ = 한 글자 (대소문자 무시) */
export function likeRe(pat) {
    const body = pat.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/[*%]/g, '.*').replace(/[?_]/g, '.');
    return new RegExp('^' + body + '$', 'i');
}
/** 조건 하나 → 행 판정 함수 (필터 빌더·코드 필터에서 사용) */
export function compilePredicate(ds, p) { return compile(ds, p); }
const cmpC = new Intl.Collator('ko', { numeric: true });
const cmp = (a, b) => cmpC.compare(a, b);
/**
 * 필터 입력칸 문법 → 조건
 *   "140" (숫자 =) · "내과" (포함) · "=내과" · "!내과" · ">=140" · "<5" · "100~200" · "내과,외과" (목록) · "=" (빈 값) · "!=" (값 있음)
 */
export function parseFilterText(col, text, numeric) {
    const t = text.trim();
    if (!t)
        return null;
    if (t === '=')
        return { col, op: 'empty' };
    if (t === '!=' || t === '!')
        return { col, op: 'notEmpty' };
    let m = t.match(/^(>=|<=|!=|>|<|=|!)\s*(.+)$/);
    if (m) {
        const op = (m[1] === '!' ? (numeric ? '!=' : 'notContains') : m[1]);
        return { col, op, value: numeric ? Number(m[2].replace(/,/g, '')) : m[2] };
    }
    m = t.match(/^(.+?)\s*~\s*(.+)$/);
    if (m)
        return { col, op: 'between', value: numeric ? Number(m[1].replace(/,/g, '')) : m[1], value2: numeric ? Number(m[2].replace(/,/g, '')) : m[2] };
    // 숫자 컬럼의 천 단위 쉼표 숫자(1,000 · -12,345.5)는 목록이 아니라 그 숫자 (14차 수정 — 예전에는 [1, 0] 목록으로 해석)
    if (numeric && /^[-+]?\d{1,3}(,\d{3})+(\.\d+)?$/.test(t))
        return { col, op: '=', value: Number(t.replace(/,/g, '')) };
    if (t.includes(','))
        return { col, op: 'in', value: t.split(',').map(s => s.trim()).filter(Boolean) };
    if (!numeric && /[*?%_]/.test(t)) {
        if (/^[^*?%_]+\*$/.test(t))
            return { col, op: 'startsWith', value: t.slice(0, -1) };
        if (/^\*[^*?%_]+$/.test(t))
            return { col, op: 'endsWith', value: t.slice(1) };
        return { col, op: 'like', value: t };
    }
    return numeric ? { col, op: '=', value: Number(t.replace(/,/g, '')) } : { col, op: 'contains', value: t };
}
/** 여러 컬럼 통합 검색 (문자열은 사전 마스크, 숫자는 문자열 포함) */
export function quickSearch(ds, view, text, cols, display) {
    const q = text.trim().toLowerCase();
    if (!q)
        return view;
    const masks = [];
    const nums = [];
    for (const id of cols) {
        const c = ds.cols[id];
        if (!c)
            continue;
        if (c instanceof StrColumn) {
            const m = new Uint8Array(c.dict.length);
            let any = false;
            for (let i = 1; i < c.dict.length; i++) {
                const s = display ? display(id, c.dict[i]) : c.dict[i];
                if (s != null && String(s).toLowerCase().includes(q)) {
                    m[i] = 1;
                    any = true;
                }
            }
            if (any)
                masks.push([c.codes, m]);
        }
        else if (/[\d.-]/.test(q))
            nums.push([id, c.vals]);
    }
    const out = new Uint32Array(view.length);
    let k = 0;
    for (let i = 0; i < view.length; i++) {
        const r = view[i];
        let hit = false;
        for (let m = 0; m < masks.length && !hit; m++)
            hit = masks[m][1][masks[m][0][r]] === 1;
        for (let m = 0; m < nums.length && !hit; m++) {
            const v = nums[m][1][r];
            hit = v === v && String(v).includes(q);
        }
        if (hit)
            out[k++] = r;
    }
    return out.slice(0, k);
}
/** 가중 평균 Σ(x·w)/Σw — 값·가중치 중 하나라도 숫자가 아니면 그 행은 뺌. valueAt 은 계산 컬럼용 */
export function weightedAvg(ds, rows, col, weightCol, valueAt) {
    let s = 0, w = 0;
    for (let i = 0; i < rows.length; i++) {
        const r = rows[i], xv = valueAt ? valueAt(r) : ds.get(r, col), wv = ds.get(r, weightCol), x = Number(xv), wt = Number(wv);
        if (xv == null || xv === '' || wv == null || wv === '' || x !== x || wt !== wt)
            continue;
        s += x * wt;
        w += wt;
    }
    return w ? s / w : null;
}
/** SummarySpec 계산 (순수) */
export function evalSummary(spec, ds, rows, col, valueAt) {
    if (spec.fn === 'weightedAvg')
        return weightedAvg(ds, rows, col, spec.weight, valueAt);
    const get = valueAt ?? ((r) => ds.get(r, col));
    return spec.fn(Array.from(rows, get), rows, ds);
}
export function aggregate(ds, view, col, fn) {
    const c = ds.cols[col];
    if (!c)
        return null;
    if (fn === 'count') {
        if (c instanceof StrColumn) {
            let n = 0;
            const codes = c.codes;
            for (let i = 0; i < view.length; i++)
                if (codes[view[i]] !== 0)
                    n++;
            return n;
        }
        let n = 0;
        const v = c.vals;
        for (let i = 0; i < view.length; i++)
            if (v[view[i]] === v[view[i]])
                n++;
        return n;
    }
    if (!(c instanceof NumColumn))
        return null;
    const v = c.vals;
    let s = 0, n = 0, mn = Infinity, mx = -Infinity;
    for (let i = 0; i < view.length; i++) {
        const x = v[view[i]];
        if (x !== x)
            continue;
        s += x;
        n++;
        if (x < mn)
            mn = x;
        if (x > mx)
            mx = x;
    }
    if (!n)
        return null;
    return fn === 'sum' ? s : fn === 'avg' ? s / n : fn === 'min' ? mn : mx;
}
/**
 * 그룹 표시 목록. 반환 disp: 0 이상이면 물리 행 번호, 음수면 groups[-(v+1)] 그룹 머리행.
 * view 는 그룹 컬럼으로 이미 정렬되어 있어야 한다 (sortView 로 그룹 키를 첫 정렬키로).
 */
export function buildGroups(ds, sortedView, groupCol, collapsed, labelOf) {
    const c = ds.cols[groupCol];
    const groups = [];
    const disp = [];
    let start = 0;
    const keyAt = (r) => (c instanceof StrColumn ? c.codes[r] : c.vals[r]);
    const flush = (end) => {
        if (end <= start)
            return;
        const rows = sortedView.slice(start, end);
        const raw = ds.get(rows[0], groupCol);
        const key = raw == null ? null : String(raw);
        const k = key ?? '∅';
        const g = { kind: 'group', key, label: labelOf ? labelOf(raw) : key ?? '(없음)', level: 0, rows, collapsed: collapsed.has(k) };
        groups.push(g);
        disp.push(-groups.length);
        if (!g.collapsed)
            for (let i = 0; i < rows.length; i++)
                disp.push(rows[i]);
        start = end;
    };
    for (let i = 1; i <= sortedView.length; i++) {
        if (i === sortedView.length || !sameKey(keyAt(sortedView[i]), keyAt(sortedView[i - 1])))
            flush(i);
    }
    return { disp: Int32Array.from(disp), groups };
}
const sameKey = (a, b) => a === b || (a !== a && b !== b);
/**
 * 다단계 그룹. sortedView 는 그룹 컬럼들 순서로 정렬돼 있어야 한다.
 * disp: 0 이상 = 물리 행, 음수 = groups[-(v+1)]. isCollapsed(path) 로 접힘 판단 (path = 상위 키들을 \u0001 로 연결)
 */
export function buildGroupsMulti(ds, sortedView, levels, isCollapsed, order) {
    const groups = [];
    const disp = [];
    const keyFns = levels.map(L => {
        const c = ds.cols[L.col];
        if (L.keyOf) {
            const f = L.keyOf;
            return (r) => f(ds.get(r, L.col));
        }
        if (c instanceof StrColumn)
            return (r) => c.codes[r];
        const v = c.vals;
        return (r) => { const x = v[r]; return x !== x ? null : x; };
    });
    const walk = (s, e, lv, prefix) => {
        const key = keyFns[lv];
        // 같은 키 구간을 모은 뒤 (요약 값 정렬이 있으면 정렬해서) 차례로 내보냄
        const segs = [];
        let start = s;
        for (let i = s + 1; i <= e; i++) {
            if (i < e && key(sortedView[i]) === key(sortedView[i - 1]))
                continue;
            segs.push([start, i]);
            start = i;
        }
        if (order && segs.length > 1) {
            const sc = new Map(segs.map(g => [g, order.score(sortedView.subarray(g[0], g[1]))]));
            const m = order.dir === 'desc' ? -1 : 1;
            segs.sort((a, b) => { const x = sc.get(a), y = sc.get(b); if (x == null || y == null)
                return x == null ? (y == null ? 0 : 1) : -1; return (x - y) * m; });
        }
        for (const [start, i] of segs) {
            const rows = sortedView.subarray(start, i);
            const r0 = rows[0];
            const L = levels[lv];
            const k = L.keyOf ? L.keyOf(ds.get(r0, L.col)) : (() => { const raw = ds.get(r0, L.col); return raw == null ? null : String(raw); })();
            const path = prefix + (k ?? '∅');
            const g = { kind: 'group', key: k, label: L.labelOf ? L.labelOf(k) : k ?? '(없음)', level: lv, rows, collapsed: isCollapsed(path), col: L.col, path };
            groups.push(g);
            disp.push(-groups.length);
            if (!g.collapsed) {
                if (lv + 1 < levels.length)
                    walk(start, i, lv + 1, path + '\u0001');
                else
                    for (let j = 0; j < rows.length; j++)
                        disp.push(rows[j]);
            }
        }
    };
    if (sortedView.length && levels.length)
        walk(0, sortedView.length, 0, '');
    return { disp: Int32Array.from(disp), groups };
}
/**
 * 그룹 푸터 끼우기: 펼친 그룹의 마지막 행 뒤에 mk(그룹 번호) 값을 넣은 새 disp.
 * disp 음수(> EXTRA)는 groups[-(v+1)] 머리행. 접힌 그룹은 푸터 없음
 */
export function withGroupFooters(disp, groups, mk) {
    const out = [], stack = [];
    const close = (lv) => { while (stack.length && groups[stack[stack.length - 1]].level >= lv)
        out.push(mk(stack.pop())); };
    for (let i = 0; i < disp.length; i++) {
        const it = disp[i];
        if (it < 0 && it > -(1 << 30)) {
            const gi = -it - 1;
            close(groups[gi].level);
            out.push(it);
            if (!groups[gi].collapsed)
                stack.push(gi);
        }
        else
            out.push(it);
    }
    close(0);
    return Int32Array.from(out);
}
/**
 * 페이지 구간: 데이터 행(과 접힌 그룹 머리행)만 페이지 크기로 셈. 펼친 그룹 머리행·그룹 푸터(<= EXTRA)는 세지 않음.
 * 그룹 푸터는 앞 페이지 끝에 붙고, 그룹 머리행은 다음 첫 데이터 행과 같은 페이지에 둠
 */
export function pageRange(disp, ps, page, unit) {
    if (ps <= 0)
        return { start: 0, end: disp.length, total: disp.length, pages: 1, page: 0 }; // 페이지 없음: 셀 필요 없음
    const pos = [];
    for (let i = 0; i < disp.length; i++)
        if (unit(disp[i]))
            pos.push(i);
    const total = pos.length, pages = Math.max(1, Math.ceil(total / ps));
    page = Math.max(0, Math.min(pages - 1, page | 0));
    const cut = (k) => {
        if (k <= 0)
            return 0;
        if (k >= total)
            return disp.length;
        let i = pos[k - 1] + 1;
        while (i < disp.length && disp[i] <= -(1 << 30))
            i++;
        return i;
    };
    return { start: cut(page * ps), end: cut((page + 1) * ps), total, pages, page };
}
